import type { MaybeRef } from 'vue'
import { computed, onBeforeUnmount, ref, unref, watch } from 'vue'
import usePlayer from '@/composables/usePlayer'
import { dismissBootSplash, isBootSplashPresent } from '@/helpers/bootSplash'
import { resolveBootPlaybackCoverMaxMs } from '@/helpers/resolveBootPlaybackCoverMaxMs'
import { resolveShouldDismissBootSplash } from '@/helpers/resolveBootSplashDismiss'

export function useBootSplashUntilPlayback(params: {
  showQuotaExceededScreen: MaybeRef<boolean>
  weeklyAvailabilityPlaybackSuppressed: MaybeRef<boolean>
}) {
  const { state } = usePlayer()

  const coverElapsedMs = ref(0)
  let coverStartedAt: number | null = null
  let coverTickTimer: ReturnType<typeof setInterval> | null = null

  function stopCoverClock(): void {
    if (coverTickTimer != null) {
      clearInterval(coverTickTimer)
      coverTickTimer = null
    }
  }

  function startCoverClock(): void {
    if (coverTickTimer != null) {
      return
    }
    if (coverStartedAt == null) {
      coverStartedAt = performance.now()
      coverElapsedMs.value = 0
    }
    coverTickTimer = setInterval(() => {
      if (coverStartedAt == null) {
        return
      }
      coverElapsedMs.value = performance.now() - coverStartedAt
    }, 100)
  }

  watch(
    () => state.tenant,
    (tenant) => {
      if (tenant != null && tenant.quotaExcess !== true) {
        startCoverClock()
        return
      }
      stopCoverClock()
      coverStartedAt = null
      coverElapsedMs.value = 0
    },
    { immediate: true }
  )

  const coverMaxMs = computed(() =>
    resolveBootPlaybackCoverMaxMs({
      weeklyAvailabilityPlaybackSuppressed: unref(params.weeklyAvailabilityPlaybackSuppressed),
      queueEmpty: state.queue.length < 1
    })
  )

  const shouldDismissBootSplash = computed(() =>
    resolveShouldDismissBootSplash({
      showQuotaExceededScreen: unref(params.showQuotaExceededScreen),
      tenant: state.tenant,
      registrationCode: state.registrationCode,
      pairingAfterUnregisterPending: state.pairingAfterUnregisterPending,
      firstPlaybackFramePainted: state.firstPlaybackFramePainted,
      coverElapsedMs: coverElapsedMs.value,
      coverMaxMs: coverMaxMs.value
    })
  )

  let dismissed = false

  watch(
    shouldDismissBootSplash,
    (dismiss) => {
      if (!dismiss || dismissed || !isBootSplashPresent()) {
        return
      }
      dismissed = true
      stopCoverClock()
      dismissBootSplash()
    },
    { immediate: true, flush: 'post' }
  )

  onBeforeUnmount(() => {
    stopCoverClock()
  })
}
